import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";

import type { RelatedNote } from "@/services/invoiceService";
import { formatDisplayDate } from "@/lib/dateFormat";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { cn } from "@/lib/utils";

export interface RelatedDocumentsListProps {
  relatedCreditNotes: RelatedNote[];
  relatedDebitNotes: RelatedNote[];
  className?: string;
}

/** Use dgiStatus when available for accurate DGI outcome display. */
function displayStatus(note: RelatedNote): string {
  if (note.dgiStatus === "confirmed") return "confirmed";
  if (note.dgiStatus === "rejected") return "rejected";
  if (note.dgiStatus === "sent") return "sent";
  return note.status;
}

function statusVariant(status: string): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "accepted":
    case "confirmed":
      return "success";
    case "rejected":
      return "danger";
    case "pending_submission":
    case "sent":
      return "warning";
    default:
      return "info";
  }
}

function formatAmount(total: number): string {
  return Number(total).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function DocTypeBadge({
  cfeType,
  typeLabel,
  variant,
}: {
  cfeType: string;
  typeLabel: string;
  variant: "credit" | "debit";
}): JSX.Element {
  const isCredit = variant === "credit";
  return (
    <Badge
      className={cn(
        "font-medium",
        isCredit
          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800"
          : "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800"
      )}
    >
      {typeLabel}
    </Badge>
  );
}

function RelatedRow({
  note,
  variant,
  typeLabel,
  onNavigate,
  statusLabel,
  displayStatus: ds,
}: {
  note: RelatedNote;
  variant: "credit" | "debit";
  typeLabel: string;
  onNavigate: (id: string) => void;
  statusLabel: string;
  displayStatus: string;
}): JSX.Element {
  const { i18n } = useTranslation();
  const cfNumber = note.series ? `${note.series}-${note.number}` : String(note.number);
  return (
    <TableRow
      className="cursor-pointer transition-colors hover:bg-muted/50"
      onRowClick={() => onNavigate(note.id)}
    >
      <TableCell>
        <DocTypeBadge cfeType={note.cfeType} typeLabel={typeLabel} variant={variant} />
      </TableCell>
      <TableCell className="font-mono text-sm">{cfNumber}</TableCell>
      <TableCell>
        {formatDisplayDate(note.issueDate, {
          locale: i18n.language,
          useCalendarDate: true,
        })}
      </TableCell>
      <TableCell className="text-right">{formatAmount(note.total)}</TableCell>
      <TableCell>
        <StatusBadge variant={statusVariant(ds)}>
          {statusLabel}
        </StatusBadge>
      </TableCell>
      <TableCell className="w-8">
        <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
      </TableCell>
    </TableRow>
  );
}

export function RelatedDocumentsList({
  relatedCreditNotes,
  relatedDebitNotes,
  className,
}: RelatedDocumentsListProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasAny = relatedCreditNotes.length > 0 || relatedDebitNotes.length > 0;

  if (!hasAny) {
    return (
      <Card className={cn(className)}>
        <CardHeader>
          <CardTitle className="text-foreground">
            {t("invoices.detail.relatedDocuments.title", "Related documents")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t(
              "invoices.detail.relatedDocuments.empty",
              "No Credit or Debit Notes have been issued for this invoice."
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t("invoices.detail.relatedDocuments.title", "Related documents")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("invoices.detail.relatedDocuments.type", "Type")}</TableHead>
              <TableHead>{t("invoices.detail.relatedDocuments.cfeNumber", "CFE number")}</TableHead>
              <TableHead>{t("invoices.detail.relatedDocuments.issueDate", "Issue date")}</TableHead>
              <TableHead className="text-right">{t("invoices.detail.relatedDocuments.amount", "Amount")}</TableHead>
              <TableHead>{t("invoices.detail.relatedDocuments.status", "Status")}</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {relatedCreditNotes.map((note) => {
              const ds = displayStatus(note);
              return (
                <RelatedRow
                  key={note.id}
                  note={note}
                  variant="credit"
                  typeLabel={t("invoices.detail.relatedDocuments.nc", "NC")}
                  onNavigate={(id) => navigate(`/invoices/${id}`)}
                  statusLabel={t(`invoices.status.${ds}`, ds)}
                  displayStatus={ds}
                />
              );
            })}
            {relatedDebitNotes.map((note) => {
              const ds = displayStatus(note);
              return (
                <RelatedRow
                  key={note.id}
                  note={note}
                  variant="debit"
                  typeLabel={t("invoices.detail.relatedDocuments.nd", "ND")}
                  onNavigate={(id) => navigate(`/invoices/${id}`)}
                  statusLabel={t(`invoices.status.${ds}`, ds)}
                  displayStatus={ds}
                />
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
